import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MACHINE_DROPDOWNS } from 'src/app/shared/constants/machine-field-options';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';
import {
  machineFormToApi,
  machineLastUpdatedValue,
  machineMasterToForm,
} from 'src/app/shared/utils/machine-master.util';

@Component({
  selector: 'app-add-machine',
  templateUrl: './add-machine.component.html',
  styleUrl: './add-machine.component.css',
})
export class AddMachineComponent implements OnInit, OnDestroy {
  machineForm!: FormGroup;
  mId: string | null = null;
  organizationId: number | string | null = null;
  organizationName = '';
  branchList: Array<{ branch_id: string | number; branch_name: string }> = [];
  machineOwners: any[] = [];
  viewMode = false;
  pageTitleKey = 'ADD_MACHINE';
  loadedName = '';
  loadedCode = '';
  saving = false;
  readonly dropdowns = MACHINE_DROPDOWNS;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private readonly apiService: ApiService,
    private router: Router,
    private toastrMsgService: ToastrMsgService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.viewMode = this.route.snapshot.data['mode'] === 'view';

    const user = localStorage.getItem('user') as string | null;
    const userParse = user ? JSON.parse(user) : { user: {} };
    this.organizationId = userParse.user?.org_id ?? userParse.user?.id ?? null;
    this.organizationName = userParse.user?.org_name ?? '';

    this.machineForm = this.fb.group({
      companyName: [this.organizationName],
      companyId: [this.organizationId],
      branchName: [''],
      branchId: [''],
      projectType: [''],
      projectId: [''],
      projectName: [''],
      projectLocation: [''],
      machineId: ['', Validators.required],
      machineName: ['', Validators.required],
      machineType: ['', Validators.required],
      machineCategory: [''],
      serialNumber: [''],
      manufacturer: [''],
      model: [''],
      yearOfManufacture: [null],
      purchaseDate: [''],
      acquisitionCost: [null],
      currentValue: [null],
      ownerType: [''],
      ownerName: [''],
      ownerContact: [''],
      ownerAddress: [''],
      machineOwnerId: [''],
      leaseStatus: [''],
      leasingCompany: [''],
      leaseStartDate: [''],
      leaseEndDate: [''],
      leaseTerms: [''],
      currentLocation: [''],
      department: [''],
      operationalStatus: [''],
      lastMaintenanceDate: [''],
      nextMaintenanceDate: [''],
      maintenanceHistory: [''],
      usageHours: [null],
      fuelType: [''],
      fuelConsumptionRate: [null],
      operatorName: [''],
      operatorLicense: [''],
      condition: [''],
      warrantyStatus: [''],
      warrantyExpiryDate: [''],
      insuranceStatus: [''],
      insuranceProvider: [''],
      insurancePolicyNumber: [''],
      depreciationRate: [null],
      sigmaAssetTag: [''],
      qrCodeId: [''],
      lastUpdated: [{ value: machineLastUpdatedValue(), disabled: true }],
      notes: [''],
    });

    this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.mId = params['id'] ?? null;
      this.pageTitleKey = this.viewMode ? 'VIEW_MACHINE' : this.mId ? 'EDIT_MACHINE' : 'ADD_MACHINE';
      if (this.mId) {
        this.fetchData(this.mId);
      }
    });

    if (this.viewMode) {
      this.machineForm.disable({ emitEvent: false });
    }

    this.fetchOrganizationsBranch();
    this.loadMachineOwners();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get pageSubtitle(): string {
    if (this.loadedName || this.loadedCode) {
      return [this.loadedCode, this.loadedName].filter(Boolean).join(' — ');
    }
    return this.organizationName;
  }

  dropdownOptions(control: string): string[] {
    return this.dropdowns.find((d) => d.formControl === control)?.options ?? [];
  }

  goToEdit(): void {
    if (!this.mId) return;
    void this.router.navigate(['/home/edit-machine', this.mId]);
  }

  loadMachineOwners(): void {
    this.apiService.listMachineOwners().subscribe({
      next: (res: any) => (this.machineOwners = res?.data || []),
      error: () => (this.machineOwners = []),
    });
  }

  onMachineOwnerSelected(ownerId: string | number): void {
    const owner = this.machineOwners.find((o) => String(o.id) === String(ownerId));
    if (!owner) {
      return;
    }
    this.machineForm.patchValue({
      machineOwnerId: owner.id,
      ownerType: owner.owner_type || this.machineForm.value.ownerType,
      ownerName: owner.owner_name || this.machineForm.value.ownerName,
      ownerContact: owner.contact_phone || owner.contact_email || this.machineForm.value.ownerContact,
      ownerAddress: owner.address || this.machineForm.value.ownerAddress,
    });
  }

  fetchOrganizationsBranch(): void {
    if (!this.organizationId) {
      return;
    }

    this.apiService.getOrganizationsBranch(this.organizationId).subscribe({
      next: (res: { data?: Array<{ branch_id: string | number; branch_name: string }> }) => {
        this.branchList = res.data ?? [];
      },
      error: () => {
        this.branchList = [];
      },
    });
  }

  onBranchSelected(branchId: string | number): void {
    const selected = this.branchList.find((b) => String(b.branch_id) === String(branchId));
    this.machineForm.patchValue({
      branchId,
      branchName: selected?.branch_name ?? '',
    });
  }

  onSubmit(): void {
    if (this.viewMode) {
      return;
    }

    this.machineForm.get('lastUpdated')?.setValue(machineLastUpdatedValue(), { emitEvent: false });

    if (this.machineForm.invalid) {
      this.machineForm.markAllAsTouched();
      this.toastrMsgService.error('Please fill all required fields.');
      return;
    }

    const raw = this.machineForm.getRawValue();
    raw.companyName = raw.companyName || this.organizationName;
    raw.companyId = raw.companyId || this.organizationId;
    const payload = machineFormToApi(raw);
    this.saving = true;

    if (this.mId) {
      this.apiService.updateMachineMaster(this.mId, payload).subscribe({
        next: () => {
          this.saving = false;
          this.toastrMsgService.success('Machine updated successfully');
          void this.router.navigate(['/home/machine-master']);
        },
        error: (err) => {
          this.saving = false;
          this.toastrMsgService.error(err?.error?.errorMessage ?? err?.error?.message ?? 'Update failed');
        },
      });
      return;
    }

    this.apiService.saveMachineMaster(payload).subscribe({
      next: () => {
        this.saving = false;
        this.toastrMsgService.success('Machine added successfully');
        void this.router.navigate(['/home/machine-master']);
      },
      error: (err) => {
        this.saving = false;
        this.toastrMsgService.error(err?.error?.errorMessage ?? err?.error?.message ?? 'Save failed');
      },
    });
  }

  fetchData(id: string): void {
    this.apiService.getMachineMasterId(id).subscribe({
      next: (res: any) => {
        const record = (res?.data as Record<string, unknown>) ?? (res as Record<string, unknown>);
        const formValue = machineMasterToForm(record);
        this.machineForm.patchValue(formValue, { emitEvent: false });
        this.loadedName = String(formValue['machineName'] ?? '');
        this.loadedCode = String(formValue['machineId'] ?? '');

        const branchId = formValue['branchId'];
        if (branchId !== undefined && branchId !== null && branchId !== '') {
          this.onBranchSelected(branchId as string | number);
        } else if (formValue['branchName']) {
          const match = this.branchList.find((b) => b.branch_name === formValue['branchName']);
          if (match) {
            this.onBranchSelected(match.branch_id);
          }
        }

        if (this.viewMode) {
          this.machineForm.disable({ emitEvent: false });
        }
      },
      error: (err) => {
        this.toastrMsgService.error(err?.error?.errorMessage ?? err?.error?.error ?? 'Failed to load machine');
      },
    });
  }

  goBack(): void {
    void this.router.navigate(['/home/machine-master']);
  }
}
